import { useState, useEffect } from 'react';
import type { TripLegResponseItem } from '../data/models/trip-leg.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { tripStatusNoLoadList } from '../data/models/trip-leg-status.model';
import { timestampsMatchToMinute } from '../utils/timestampValidation';

/**
 * Type for optimistic timestamp updates
 */
export interface OptimisticTimestamps {
  [tripLegReferenceID: string]: {
    [field: string]: string;
  };
}

/**
 * Type for optimistic status updates with associated timestamps
 */
export interface OptimisticStatuses {
  [tripLegReferenceID: string]: {
    status: TripLegStatusBC;
    timestamps: {
      onSite?: string;
      onBoard?: string;
      completed?: string;
      noLoad?: string;
      canceledLate?: string;
    };
  };
}

/**
 * Custom hook for managing optimistic UI updates for trip leg timestamps and statuses.
 *
 * This hook manages two types of optimistic updates:
 * 1. Individual timestamp updates (onSite, onBoard, completed, etc.)
 * 2. Status changes that affect multiple timestamps at once
 *
 * The hook automatically clears optimistic updates when the backend confirms them.
 *
 * @param tripLegs - The current list of trip legs from the backend
 * @returns Optimistic state and update handlers
 */
export function useOptimisticUpdates(tripLegs: TripLegResponseItem[]) {
  const [optimisticTimestamps, setOptimisticTimestamps] =
    useState<OptimisticTimestamps>({});

  const [optimisticStatuses, setOptimisticStatuses] =
    useState<OptimisticStatuses>({});

  /**
   * Updates an optimistic timestamp for a specific trip leg and field
   */
  const updateOptimisticTimestamp = (
    tripLegReferenceID: string,
    field: string,
    timestamp: string,
  ) => {
    setOptimisticTimestamps((prev) => ({
      ...prev,
      [tripLegReferenceID]: {
        ...prev[tripLegReferenceID],
        [field]: timestamp,
      },
    }));
  };

  /**
   * Updates an optimistic status change with associated timestamps
   */
  const updateOptimisticStatus = (
    tripLegReferenceID: string,
    status: TripLegStatusBC,
    timestamps: {
      onSite?: string;
      onBoard?: string;
      completed?: string;
      noLoad?: string;
      canceledLate?: string;
    },
  ) => {
    setOptimisticStatuses((prev) => ({
      ...prev,
      [tripLegReferenceID]: {
        status,
        timestamps,
      },
    }));
  };

  /**
   * Clears optimistic timestamps when backend confirms the update
   */
  useEffect(() => {
    setOptimisticTimestamps((prev) => {
      const updated = { ...prev };
      Object.keys(prev).forEach((tripLegReferenceID) => {
        const tripLeg = tripLegs.find(
          (tl) => tl.tripLegReferenceID === tripLegReferenceID,
        );
        if (tripLeg) {
          const fields = prev[tripLegReferenceID];
          const shouldClear: { [field: string]: boolean } = {};

          Object.keys(fields).forEach((field) => {
            const optimisticTime = fields[field];
            let backendTime: string | undefined;

            // Find the backend timestamp for this field
            if (field === 'onSite') {
              backendTime = tripLeg.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Onsite,
              )?.createdOnUTC;
            } else if (field === 'onBoard') {
              backendTime = tripLeg.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Onboard,
              )?.createdOnUTC;
            } else if (field === 'completed') {
              backendTime = tripLeg.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Completed,
              )?.createdOnUTC;
            } else if (field === 'noLoad') {
              backendTime = tripLeg.tripLegStatusLogs?.find((log) =>
                tripStatusNoLoadList.includes(
                  log.tripLegStatusBC as TripLegStatusBC,
                ),
              )?.createdOnUTC;
            } else if (field === 'canceledLate') {
              backendTime = tripLeg.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.CanceledLate,
              )?.createdOnUTC;
            }

            // Only clear the optimistic timestamp if the backend has confirmed
            // the same timestamp (matching to the minute)
            if (backendTime && optimisticTime) {
              if (timestampsMatchToMinute(optimisticTime, backendTime)) {
                shouldClear[field] = true;
              }
            }
          });

          Object.keys(shouldClear).forEach((field) => {
            delete updated[tripLegReferenceID][field];
          });

          if (Object.keys(updated[tripLegReferenceID]).length === 0) {
            delete updated[tripLegReferenceID];
          }
        }
      });
      return updated;
    });
  }, [tripLegs]);

  /**
   * Clears optimistic status changes when backend confirms the new status
   */
  useEffect(() => {
    setOptimisticStatuses((prev) => {
      const updated = { ...prev };
      Object.keys(prev).forEach((tripLegReferenceID) => {
        const tripLeg = tripLegs.find(
          (tl) => tl.tripLegReferenceID === tripLegReferenceID,
        );
        if (tripLeg) {
          const optimisticStatus = prev[tripLegReferenceID];
          if (tripLeg.tripLegStatusBC === optimisticStatus.status) {
            let allTimestampsMatch = true;
            const timestamps = optimisticStatus.timestamps;

            // Check onSite timestamp
            if (timestamps.onSite && allTimestampsMatch) {
              const backendTime =
                tripLeg.tripLegStatusLogs?.find(
                  (log) => log.tripLegStatusBC === TripLegStatusBC.Onsite,
                )?.createdOnUTC || '';
              if (!timestampsMatchToMinute(timestamps.onSite, backendTime)) {
                allTimestampsMatch = false;
              }
            }

            // Check onBoard timestamp
            if (timestamps.onBoard && allTimestampsMatch) {
              const backendTime =
                tripLeg.tripLegStatusLogs?.find(
                  (log) => log.tripLegStatusBC === TripLegStatusBC.Onboard,
                )?.createdOnUTC || '';
              if (!timestampsMatchToMinute(timestamps.onBoard, backendTime)) {
                allTimestampsMatch = false;
              }
            }

            // Check completed timestamp
            if (timestamps.completed && allTimestampsMatch) {
              const backendTime =
                tripLeg.tripLegStatusLogs?.find(
                  (log) => log.tripLegStatusBC === TripLegStatusBC.Completed,
                )?.createdOnUTC || '';
              if (!timestampsMatchToMinute(timestamps.completed, backendTime)) {
                allTimestampsMatch = false;
              }
            }

            // Check noLoad timestamp
            if (timestamps.noLoad && allTimestampsMatch) {
              const backendTime = tripLeg.tripLegStatusLogs?.find((log) =>
                tripStatusNoLoadList.includes(
                  log.tripLegStatusBC as TripLegStatusBC,
                ),
              )?.createdOnUTC;
              if (!timestampsMatchToMinute(timestamps.noLoad, backendTime)) {
                allTimestampsMatch = false;
              }
            }

            // Check canceledLate timestamp
            if (timestamps.canceledLate && allTimestampsMatch) {
              const backendTime =
                tripLeg.tripLegStatusLogs?.find(
                  (log) => log.tripLegStatusBC === TripLegStatusBC.CanceledLate,
                )?.createdOnUTC || '';
              if (
                !timestampsMatchToMinute(timestamps.canceledLate, backendTime)
              ) {
                allTimestampsMatch = false;
              }
            }

            // Only clear if status and all timestamps match
            if (allTimestampsMatch) {
              delete updated[tripLegReferenceID];
            }
          }
        }
      });
      return updated;
    });
  }, [tripLegs]);

  const clearOptimisticTimestampsForTrip = (tripLegReferenceID: string) => {
    setOptimisticTimestamps((prev) => {
      if (!prev[tripLegReferenceID]) return prev;
      const updated = { ...prev };
      delete updated[tripLegReferenceID];
      return updated;
    });
  };

  return {
    optimisticTimestamps,
    optimisticStatuses,
    updateOptimisticTimestamp,
    updateOptimisticStatus,
    clearOptimisticTimestampsForTrip,
  };
}
